import Card from "components/card";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { IoPencil, IoCheckmark, IoClose, IoChevronDown } from "react-icons/io5";
import { Country, State, City } from "country-state-city";
import { handleComponentSave } from "./saveHelper";
import { usePermission } from "hooks/usePermission";

const General = ({ lead, updateLead, showToast, setCurrentLead }) => {
  const { can } = usePermission();
  const canUpdateLead = can("lead_management", "update");
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchByDropdown, setSearchByDropdown] = useState({});
  const dropdownRef = useRef();

  const countryOptions = useMemo(
    () => Country.getAllCountries().map((country) => country.name),
    [],
  );

  const selectedCountry = useMemo(
    () => Country.getAllCountries().find((country) => country.name === editedData.country),
    [editedData.country],
  );

  const stateOptions = useMemo(() => {
    if (!selectedCountry) return [];
    return State.getStatesOfCountry(selectedCountry.isoCode).map((state) => state.name);
  }, [selectedCountry]);

  const selectedState = useMemo(() => {
    if (!selectedCountry || !editedData.state) return null;
    return State.getStatesOfCountry(selectedCountry.isoCode).find(
      (state) => state.name === editedData.state,
    );
  }, [selectedCountry, editedData.state]);

  const districtOptions = useMemo(() => {
    if (!selectedCountry || !selectedState) return [];
    const cities = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode);
    return Array.from(new Set(cities.map((city) => city.name))).sort();
  }, [selectedCountry, selectedState]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = (key, disabled = false) => {
    if (disabled) return;
    setOpenDropdown((prev) => (prev === key ? null : key));
    setSearchByDropdown((prev) => ({ ...prev, [key]: "" }));
  };

  const getFilteredOptions = (key, options) => {
    const term = (searchByDropdown[key] || "").trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.toLowerCase().includes(term));
  };

  const renderLocationDropdown = ({ keyName, label, value, options, onSelect, onClear, disabled }) => {
    const filteredOptions = getFilteredOptions(keyName, options);

    return (
      <div className="relative w-full">
        <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
          {label}
        </label>
        <button
          type="button"
          onClick={() => toggleDropdown(keyName, disabled)}
          className={`flex h-10 w-full items-center justify-between rounded-lg border px-3 text-left text-sm dark:text-white ${
            disabled
              ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 dark:border-white/10 dark:bg-gray-800/70 dark:text-gray-500"
              : "border-gray-200 bg-white hover:border-gray-300 dark:border-white/10 dark:bg-white/5"
          }`}
        >
          <span className="truncate">{value || `Select ${label.toLowerCase()}`}</span>
          <IoChevronDown className="h-4 w-4 text-gray-500" />
        </button>

        {openDropdown === keyName && !disabled && (
          <div className="absolute top-full z-20 mt-2 w-full rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-800">
            <input
              type="text"
              value={searchByDropdown[keyName] || ""}
              onChange={(e) =>
                setSearchByDropdown((prev) => ({ ...prev, [keyName]: e.target.value }))
              }
              placeholder={`Search ${label.toLowerCase()}`}
              className="mb-2 h-9 w-full rounded-md border border-gray-200 px-2 text-sm outline-none focus:border-brand-500 dark:border-white/10 dark:bg-gray-700 dark:text-white"
            />
            <button
              type="button"
              onClick={() => {
                onClear();
                setOpenDropdown(null);
              }}
              className="mb-1 h-8 w-full rounded-md px-2 text-left text-sm text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Clear
            </button>
            <div className="max-h-44 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <p className="px-2 py-2 text-xs text-gray-500 dark:text-gray-400">No options found</p>
              ) : (
                filteredOptions.map((option) => (
                  <button
                    type="button"
                    key={option}
                    onClick={() => {
                      onSelect(option);
                      setOpenDropdown(null);
                    }}
                    className={`h-8 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                      value === option
                        ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                        : "text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    {option}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const fields = [
    { label: "Email", key: "email", type: "text" },
    { label: "Phone", key: "phone", type: "text" },
    { label: "House Name / Number", key: "houseNameOrNumber", type: "text" },
    { label: "Locality / Landmark", key: "localityOrLandmark", type: "text" },
    { label: "Country", key: "country", type: "dropdown" },
    { label: "State", key: "state", type: "dropdown" },
    { label: "District", key: "district", type: "dropdown" },
    { label: "Pin Code", key: "pinCode", type: "text" },
  ];

  // Initialize data on mount
  useEffect(() => {
    if (lead) {
      const initialData = {
        email: lead.email ?? "",
        phone: lead.phone ?? "",
        houseNameOrNumber: lead.houseNameOrNumber ?? "",
        localityOrLandmark: lead.localityOrLandmark ?? "",
        district: lead.district ?? "",
        state: lead.state ?? "",
        country: lead.country ?? "",
        pinCode: lead.pinCode ?? "",
      };
      setEditedData(initialData);
      setOriginalData(initialData);
    }
  }, [lead]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await handleComponentSave(
        lead,
        editedData,
        updateLead,
        showToast,
        setCurrentLead
      );
      if (result && result.success) {
        setIsEditing(false);
        setOriginalData(editedData);
        setFieldErrors({});
      } else if (result && result.errors) {
        setFieldErrors(result.errors);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedData(originalData);
    setIsEditing(false);
  };

  return (
    <Card extra={"w-full h-full p-3"}>
      {/* Header */}
      <div className="mb-8 mt-2 w-full flex items-center justify-between">
        <h4 className="px-2 text-xl font-bold text-navy-700 dark:text-white">
          General Information
        </h4>
        {!isEditing ? (
          canUpdateLead && (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-3 py-1.5 text-white hover:bg-brand-600 transition-colors"
              title="Edit general info"
            >
              <IoPencil className="text-xl" />
            </button>
          )
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="flex items-center gap-2 rounded-lg bg-gray-500 px-3 py-1.5 text-white hover:bg-gray-600 disabled:opacity-60 transition-colors"
            >
              <IoClose className="text-lg" />
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 rounded-lg bg-green-500 px-3 py-1.5 text-white hover:bg-green-600 disabled:opacity-60 transition-colors"
            >
              <IoCheckmark className="text-lg" />
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        )}
      </div>
      {/* Cards */}
      <div ref={dropdownRef} className="grid grid-cols-2 gap-4 px-2">
        {fields.map((field) => (
          <div
            key={field.key}
            className="flex flex-col items-start justify-center rounded-2xl bg-white bg-clip-border px-3 py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none"
          >
            {isEditing && field.type === "dropdown" ? (
              renderLocationDropdown({
                keyName: field.key,
                label: field.label,
                value: editedData[field.key],
                options:
                  field.key === "country"
                    ? countryOptions
                    : field.key === "state"
                    ? stateOptions
                    : districtOptions,
                onSelect: (option) => {
                  setEditedData((prev) => {
                    const updated = { ...prev, [field.key]: option };
                    if (field.key === "country") {
                      updated.state = "";
                      updated.district = "";
                    } else if (field.key === "state") {
                      updated.district = "";
                    }
                    return updated;
                  });
                },
                onClear: () => {
                  setEditedData((prev) => {
                    const updated = { ...prev, [field.key]: "" };
                    if (field.key === "country") {
                      updated.state = "";
                      updated.district = "";
                    } else if (field.key === "state") {
                      updated.district = "";
                    }
                    return updated;
                  });
                },
                disabled:
                  (field.key === "state" && !editedData.country) ||
                  (field.key === "district" && !editedData.state),
              })
            ) : (
              <>
                <div className="flex items-center justify-between w-full mb-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {field.label}
                  </p>
                </div>
                {isEditing ? (
                  field.key === "phone" ? (
                    <input
                      type="tel"
                      value={editedData[field.key] || ""}
                      onChange={(e) =>
                        setEditedData((prev) => ({
                          ...prev,
                          [field.key]: e.target.value,
                        }))
                      }
                      className="bg-transparent border-transparent w-full border-b text-base font-medium text-navy-700 hover:border-gray-300 focus:border-brand-500 focus:outline-none dark:text-white"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                    />
                  ) : field.key === "email" ? (
                    <input
                      type="email"
                      value={editedData[field.key] || ""}
                      onChange={(e) =>
                        setEditedData((prev) => ({
                          ...prev,
                          [field.key]: e.target.value,
                        }))
                      }
                      className="bg-transparent border-transparent w-full border-b text-base font-medium text-navy-700 hover:border-gray-300 focus:border-brand-500 focus:outline-none dark:text-white"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={editedData[field.key] || ""}
                      onChange={(e) =>
                        setEditedData((prev) => ({
                          ...prev,
                          [field.key]: e.target.value,
                        }))
                      }
                      className="bg-transparent border-transparent w-full border-b text-base font-medium text-navy-700 hover:border-gray-300 focus:border-brand-500 focus:outline-none dark:text-white"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                    />
                  )
                ) : (
                  <p
                    className="text-base font-medium text-navy-700 dark:text-white truncate w-full"
                    title={lead?.[field.key] || "-"}
                  >
                    {lead?.[field.key] || "-"}
                  </p>
                )}
                {isEditing && fieldErrors[field.key] && (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors[field.key]}</p>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};

export default General;
